import React, { useContext, useEffect, useState } from 'react';
import { Stack, TextField, Typography } from '@mui/material';
import { NextPrevButton } from 'Components';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useAppSelector, useDictionary, useIsDesktop } from 'Hooks';
import { ControlledPhoneInput } from 'Components/ControlledPhoneInput';
import { requiredMessage } from 'Regex';
import { scrollToTop } from 'Utils/scrollToTop';
import { CannabisContext } from 'Context/CannabisContext';
import { maxPhoneDigits } from 'Config';

const MAX_LENGTH = 500;

export const AdditionalDetailsForm = () => {
  const { formData, setFormData, handleNextStep, handlePrevStep } =
    useContext(CannabisContext);
  const [phoneError, setPhoneError] = useState<{
    prefix: string;
    number: string;
  }>({ prefix: '', number: '' });

  const dictionary = useDictionary(Dictionary, DICTIONARY.Reimbursements);
  const isDesktop = useIsDesktop();

  const generalOptions = useAppSelector(
    (generalOptions) => generalOptions.generalOptions.generalOptions
  );

  const handleAdditionalDetailsChange = (
    e: React.ChangeEvent<HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      additionalDetails: e.target.value.slice(0, 500),
    });
  };

  const handleSubmit = () => {
    if (!!phoneError.number || !!phoneError.prefix) return;

    handleNextStep();
  };

  const phoneOnBlur = () => {
    if (!!formData.phone?.prefix && !formData.phone?.number)
      setPhoneError({ number: requiredMessage, prefix: '' });
    if (!formData.phone?.prefix && !!formData.phone?.number)
      setPhoneError({ prefix: requiredMessage, number: '' });
    if (
      (!!formData.phone?.prefix && !!formData.phone?.number) ||
      (!formData.phone?.prefix && !formData.phone?.number)
    )
      setPhoneError({ number: '', prefix: '' });
    if (
      formData.phone?.number &&
      formData.phone.number.length > 0 &&
      formData.phone.number.length < maxPhoneDigits
    )
      setPhoneError({ number: requiredMessage, prefix: '' });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  return (
    <Stack spacing={2} maxWidth={'800px'} m={'0 auto'}>
      <Typography
        variant={'h3'}
        tabIndex={0}
        component='label'
        htmlFor='request-note'
        textAlign={'center'}
        mb={3}
      >
        {dictionary.additionalDetailsTitle}
      </Typography>
      <TextField
        required
        aria-label={dictionary.notePlaceholder}
        multiline
        rows={4}
        variant='outlined'
        placeholder={dictionary.notePlaceholder}
        value={formData.additionalDetails}
        onChange={handleAdditionalDetailsChange}
        style={{ color: '#A020FF' }}
        inputProps={{
          maxLength: MAX_LENGTH,
        }}
        InputProps={{
          style: {
            background: '#fff',
            border: '1px solid #B7CCFF',
            borderRadius: '8px',
          },
        }}
        sx={{
          '& .MuiInputBase-input': {
            zIndex: 9,
          },
          '& .MuiFormHelperText-root': {
            position: 'relative',
            top: isDesktop ? '-40px' : '-30px',
            fontSize: isDesktop ? '18px' : '14px',
            zIndex: 0,
          },
          '.MuiOutlinedInput-notchedOutline': { border: 'none' },
        }}
        fullWidth
        margin='normal'
      />
      <Typography
        variant='body7'
        sx={{
          color: '#A020FF',
          textAlign: 'right',
          width: 'fit-content',
          display: 'flex',
          alignSelf: 'flex-end',
          position: 'relative',
          top: '-50px',
          right: '15px',
        }}
      >
        {formData.additionalDetails?.length || 0}/{MAX_LENGTH}
      </Typography>
      <Typography variant='body2'>{dictionary.yourPhone}</Typography>
      <ControlledPhoneInput
        onBlur={phoneOnBlur}
        prefixes={generalOptions?.general.phonePrefixes || []}
        prefix={{
          prefixValue: formData.phone?.prefix,
          handleChange: (e) =>
            setFormData({
              ...formData,
              phone: { ...formData.phone, prefix: parseInt(e.target.value) },
            }),
          error: phoneError.prefix,
          label: '',
          noPrefixOptionLabel: dictionary.prefix,
        }}
        number={{
          numberValue: formData.phone?.number,
          handleChange: (e) =>
            setFormData({
              ...formData,
              phone: { ...formData.phone, number: e.target.value },
            }),
          error: phoneError.number,
          label: dictionary.phoneNumber,
        }}
      />
      <Stack
        direction='row'
        justifyContent='center'
        spacing={isDesktop ? 6 : 1}
        sx={{ mt: '60px !important' }}
      >
        <NextPrevButton
          callback={handlePrevStep}
          text={dictionary.back}
          style={
            isDesktop
              ? undefined
              : {
                  button: { height: '36px' },
                  text: { fontSize: '16px', fontWeight: '600' },
                }
          }
        />
        <NextPrevButton
          callback={handleSubmit}
          text={dictionary.save}
          style={
            isDesktop
              ? undefined
              : {
                  button: { height: '36px', width: '100%' },
                  text: { fontSize: '16px', fontWeight: '600' },
                }
          }
          isBlue
          isNext
        />
      </Stack>
    </Stack>
  );
};
